import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import { Col, Row, Skeleton, Space } from "antd";
import _ from "lodash";

import Text from "common/components/general/Text";
import { SectionTitle } from "../../components/sectionTitle/SectionTitle";
import ClassSlider, { ClassItem } from "../../components/classSlider/ClassSlider";
import img1 from "assets/images/main-classes-img1.svg";
import EnrollmentDto from "dtos/administration/enrollment.dto";
import ClassroomDto from "dtos/administration/classroom.dto";
import { COURSE_BUILDER_PATH } from "scenes/courseBuilder";
import { SUBJECTS_PATH } from "scenes/subjects";
import { COURSE_PATH } from "scenes/course";
import { COURSE_PREVIEW_PATH } from "scenes/subjects/scenes/subjectItems/scenes/courseItem/CourseItem";
import { previewFile } from "../../../courseBuilder/utils/FileUtils";
import OAuth2Service from "services/authentication/oauth2.service";
import EnrollmentService from "services/administration/enrollment.service";
import ClassroomService from "services/administration/classroom.service";
import OrganizationService from "services/administration/organization.service";
import { TutoringModal } from "./components/tutoringModal";
import { Role } from "services/domain/login/Role";
import JoinCreateClassModal, { TypeOfClassroom } from "../../components/joinCreateClassModal/JoinCreateClassModal";
import { AsynchCoursesEmptyState } from "./components/AsynchCoursesEmptyState";
import { VirtualCoursesEmptyState } from "./components/VirtualCoursesEmptyState";
import { OrganizationContentType } from "services/domain/administration/Organization";
import { TypeOfClassroomBasedOnEnrollments } from "services/domain/administration/Classroom";
import { boolVal, getComponentProps, isComponentVisible } from "../../../../common/utils/Design";
import { MainLayout } from "scenes/main/components/layout/Layout";

export const COURSES_PATH = "courses";

const TRANSLATION_BASE_PATH = "_MAIN._CLASSES";
const teacherRoleID = Role.Teacher;

class CoursesComponent extends Component<any, any> {
	private _isMounted: boolean;

	constructor(props: any) {
		super(props);
		this.state = {
			loading: false,
			classItems: null,
			asynchClassItems: null,
			teacherHasJoinedOrg: false,
			formInitialValues: {}
		};
		this._isMounted = false;
	}

	getPhotoUrls = async (items: any): Promise<any> => {
		return (
			(await Promise.all(
				items.map(async function(classroom: any) {
					const image = (classroom.File && (await previewFile(classroom.File))) || null;
					return { ...classroom, filePath: image && image.url };
				})
			).then(classroom => {
				return classroom;
			})) || []
		);
	};

	getVirutalClassItemsForTeacher = async (data: ClassroomDto[]): Promise<ClassItem[]> => {
		if (!data) return [];
		const teacherCourseBuilderPath = `/${COURSE_BUILDER_PATH}/${teacherRoleID.toLowerCase()}/`;
		const individualCourseBuilderPath = `/${COURSE_BUILDER_PATH}/individual/`;
		const studentCoursePath = `/${COURSE_PATH}/_student/`;

		const items = data
			.filter((x: ClassroomDto) => {
				return x.isAsync === false;
			})
			.map((x: ClassroomDto) => {
				let pathToClassroom = "";
				if (x.createdBy !== OAuth2Service.CurrentUser.UserId) {
					pathToClassroom = studentCoursePath + x.id;
				} else if (
					x.virtualClassroomType &&
					x.virtualClassroomType === TypeOfClassroomBasedOnEnrollments.Individual
				) {
					pathToClassroom = individualCourseBuilderPath + x.id;
				} else {
					pathToClassroom = teacherCourseBuilderPath + x.id;
				}
				return {
					id: x.id,
					showAsTeacher: x.createdBy === OAuth2Service.CurrentUser.UserId,
					name: x.name,
					teacherName: x.UserCreatedBy?.firstName + " " + x.UserCreatedBy?.lastName,
					File: x.File,
					isAsync: false,
					pathToClassroom: pathToClassroom,
					organizationName: x.Organization?.name,
					totalEnrollments: x.TotalEnrollments,
					classroomStatus: x.status,
					virtualClassroomType: x.virtualClassroomType
				};
			});

		return this.getPhotoUrls(items);
	};

	getAsynchClassItemsForTeacher = async (data: ClassroomDto[]): Promise<ClassItem[]> => {
		if (!data) return [];
		const teacherCourseBuilderPath = `/${COURSE_BUILDER_PATH}/asynch/`;

		const items = data
			.sort((a, b) => {
				return new Date("" + b.createdAt).getTime() -
					new Date("" + a.createdAt).getTime();
			})
			.filter((x: ClassroomDto) => {
				return x.isAsync === true;
			})
			.map((x: ClassroomDto) => {
				return {
					id: x.id,
					showAsTeacher: true,
					name: x.name,
					teacherName: x.UserCreatedBy?.firstName + " " + x.UserCreatedBy?.lastName,
					File: x.File,
					isAsync: true,
					pathToClassroom: teacherCourseBuilderPath + x.id,
					status: x.status,
					organizationName: x.Organization?.name,
					totalEnrollments: x.TotalEnrollments
				};
			});

		return this.getPhotoUrls(items);
	};

	getVirutalClassItemsForStudent = async (data: any[]): Promise<ClassItem[]> => {
		const formatDate = (input: string) => {
			let result = "";
			try {
				result = input
					.split("T")[0]
					.split("-")
					.reverse()
					.join(".");
			} catch {
				return input;
			}
			return result;
		};

		const items = _.orderBy(
			data
				.sort((a, b) => {
					return new Date("" +b.createdAt).getTime() -
					new Date("" + a.createdAt).getTime();
				})
				.filter((x: EnrollmentDto) => {
					return (
						(x.status === "ACTIVE" || x.status === "PENDING") &&
						x.Classroom?.isAsync === false &&
						x.UserId === OAuth2Service.CurrentUser.UserId
					);
				})
				.map((x: EnrollmentDto) => {
					return {
						id: x.ClassroomId,
						showAsTeacher: false,
						name: x.Classroom?.name,
						teacherName: x.Classroom?.UserCreatedBy?.firstName + " " + x.Classroom?.UserCreatedBy?.lastName,
						lecturerName: "lecturer",
						File: x.Classroom?.File,
						isAsync: false,
						pathToClassroom: `/${COURSE_PATH}/_student/${x.ClassroomId}`,
						status: x.status,
						requestDate: formatDate(x.enrolledAt.toString()),
						organizationName: x.Classroom?.Organization?.name,
						totalEnrollments: x.Classroom?.TotalEnrollments
					};
				}),
			"status"
		);

		return this.getPhotoUrls(items);
	};

	getAsyncClassItemsForCurrentUser = async (data: EnrollmentDto[]): Promise<ClassItem[]> => {
		const items = data
			.filter((x: EnrollmentDto) => {
				return (
					x.status === "ACTIVE" &&
					x.Classroom?.isAsync === true &&
					x.UserId === OAuth2Service.CurrentUser.UserId
				);
			})
			.map((x: EnrollmentDto) => {
				return {
					id: x.ClassroomId,
					showAsTeacher: false,
					name: x.Classroom?.name,
					lecturerName: "lecturer",
					File: x.Classroom?.File,
					isAsync: true,
					pathToClassroom: `/${SUBJECTS_PATH}/${COURSE_PREVIEW_PATH}/${x.ClassroomId}`,
					status: x.status,
					organizationName: x.Classroom?.Organization?.name,
					totalEnrollments: x.Classroom?.TotalEnrollments
				};
			});

		return this.getPhotoUrls(items);
	};

	componentDidMount() {
		this._isMounted = true;
		this.setState({
			loading: true
		});

		const requests: any[] = [
			new EnrollmentService().getAllEnrollments(),
			new ClassroomService().getAllMyClassrooms()
		];

		return Promise.all(requests)
			.then(([allEnrollments, allClassrooms]) => {
				return Promise.all([
					this.getVirutalClassItemsForTeacher([
						...allClassrooms,
						...(allEnrollments
							? allEnrollments
								.filter(
									(item: any) =>
										!item.Classroom?.isAsync && item.UserId === OAuth2Service.CurrentUser.UserId
								)
								.map((item: any) => item.Classroom)
							: [])
					]),
					this.getAsynchClassItemsForTeacher(allClassrooms as ClassroomDto[]),
					this.getAsyncClassItemsForCurrentUser(allEnrollments as EnrollmentDto[]),
					new OrganizationService().findCurrentOrganization()
				]);
			})
			.then(([virtualClass, myAsynchClassrooms, asyncClass, organization]) => {
				return (
					this._isMounted &&
					this.setState({
						classItems: virtualClass,
						myAsynchClassrooms: myAsynchClassrooms,
						asynchClassItems: asyncClass,
						teacherHasJoinedOrg: organization.length > 0
					})
				);
			})
			.finally(() => {
				this._isMounted && this.setState({ loading: false });
			});
	}

	componentWillUnmount() {
		this._isMounted = false;
	}

	toggleTutoringModal = () => {
		this.setState({ isTutoringModalVisible: !this.state.isTutoringModalVisible });
	};

	getVirtualClassroomsContent() {
		const { t: translate } = this.props;
		const noDataTextVC = translate(`${TRANSLATION_BASE_PATH}._NO_DATA_DESC._VC.${teacherRoleID}`);
		if (
			[OrganizationContentType.All, OrganizationContentType.Live].includes(
				OAuth2Service.CurrentOrganization?.contentType
			) &&
			this.state.classItems
		) {
			let title = translate(`${TRANSLATION_BASE_PATH}._VC_TITLE`);
			if (this.state.classItems && this.state.classItems.length > 0) {
				title = (
					<Space>
						<Text fontSize="16" lineHeight="24" wheight="semibold" className="color-gray-9">
							{translate(`${TRANSLATION_BASE_PATH}._VC_TITLE`)}
						</Text>
						<JoinCreateClassModal
							history={this.props.history}
							typeOfClassroomToCreate={TypeOfClassroom.Virtual}
						/>
					</Space>
				);
			}
			return (
				<ClassSlider
					title={title}
					items={this.state.classItems}
					defaultImg={img1}
					noDataDesc={noDataTextVC}
					emptyState={<VirtualCoursesEmptyState />}
				/>
			);
		}
		return null;
	}

	getMyAsynchClassroomsContent() {
		const { t: translate } = this.props;
		if (
			[OrganizationContentType.All, OrganizationContentType.Offline].includes(
				OAuth2Service.CurrentOrganization?.contentType
			) &&
			this.state.myAsynchClassrooms
		) {
			let title = translate(`${TRANSLATION_BASE_PATH}._MY_AC_TITLE`);
			if (this.state.myAsynchClassrooms && this.state.myAsynchClassrooms.length > 0) {
				title = (
					<Space>
						<Text fontSize="16" lineHeight="24" wheight="semibold" className="color-gray-9">
							{translate(`${TRANSLATION_BASE_PATH}._MY_AC_TITLE`)}
						</Text>
						<JoinCreateClassModal
							history={this.props.history}
							typeOfClassroomToCreate={TypeOfClassroom.Asynch}
						/>
					</Space>
				);
			}
			return (
				<ClassSlider
					title={title}
					items={this.state.myAsynchClassrooms}
					defaultImg={img1}
					emptyState={<AsynchCoursesEmptyState />}
				/>
			);
		}
		return null;
	}

	render() {
		const { t: translate } = this.props;

		const noDataTextAC = translate(`${TRANSLATION_BASE_PATH}._NO_DATA_DESC._AC.${teacherRoleID}`);
		const subTitle = translate(`${TRANSLATION_BASE_PATH}._SUBTITLE`);
		const titleSliderAC = translate(`${TRANSLATION_BASE_PATH}._AC_TITLE`);
		const tutoringButtonTitle = translate(`${TRANSLATION_BASE_PATH}._BTN_TUTORING_TEACHER`);
		const sideLayoutProps = getComponentProps(this.props.pageComponents, "sideLayout");
		const headerProps = getComponentProps(this.props.pageComponents, "header");
		const showHeader: boolean = isComponentVisible(this.props.pageComponents, "header");


		return (
			<MainLayout
				showMarketPlace={boolVal(sideLayoutProps, "showExploreCourses", true)}
				showAddCourse={boolVal(sideLayoutProps, "showAddCourse", true)}
			>
				{showHeader && <SectionTitle
					subtitle={subTitle}
					tutoringButtonTitle={boolVal(headerProps, "showPrivateCourse", true) ? tutoringButtonTitle : null}
					onTutoringBtnClick={this.toggleTutoringModal}
				/>}
				{this.state.isTutoringModalVisible && <TutoringModal toggleTutoringModal={this.toggleTutoringModal} />}
				<Skeleton active loading={this.state.loading}>
					{this.getVirtualClassroomsContent()}
				</Skeleton>
				<Skeleton active loading={this.state.loading}>
					{this.getMyAsynchClassroomsContent()}
				</Skeleton>
				<Skeleton active loading={this.state.loading}>
					{this.state.asynchClassItems && !this.state.loading && (
						<ClassSlider
							title={titleSliderAC}
							items={this.state.asynchClassItems}
							defaultImg={img1}
							noDataDesc={noDataTextAC}
						/>
					)}
				</Skeleton>
				<Row className="mb-24">
					<Col span={24}></Col>
				</Row>
			</MainLayout>
		);
	}
}

const TeacherCourses = withTranslation()(CoursesComponent);
export default TeacherCourses;
